import {useState} from 'react'
import {MyApprovalsTabId} from '@/testids/MyApprovalsTestIds'
import {ContentArea} from '@/components/shared/ContentArea'
import {useQuery} from '@tanstack/react-query'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {useDialog} from '@/hooks/useDialog'
import {Clock, CheckCircle, XCircle, KeyRound, User, MessageSquare, Wand2, Layers, Shield} from 'lucide-react'
import {useNavigate} from 'react-router-dom'
import {useAuth} from '@/contexts/auth-context'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {Strong} from '@/components/shared/Strong'
import {Stack} from '@/components/shared/Stack'
import {Button} from '@/components/ui/button'
import {Badge} from '@/components/ui/badge'
import {RiskLevelBadge} from '@/components/ui/risk-level-badge'
import {Card, CardContent} from '@/components/ui/card'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import {DialogForm} from '@/components/shared/DialogForm'
import {Textarea} from '@/components/ui/textarea'
import {Label} from '@/components/ui/label'
import {Checkbox} from '@/components/ui/checkbox'
import {ErrorBanner} from '@/components/ui/error-banner'
import {Pagination} from '@/components/ui/pagination'
import {useServerPagination, SortDir} from '@/hooks/use-server-pagination'
import {AccessRequestControllerMyApprovalsEndpoint} from '@/types/endpoint-contracts'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {RequestItemStatus, ApprovalDecisionBadge} from '@/types/access-request'
import type {AccessRequestItem, BatchApproveRequest, BatchRejectRequest} from '@/types/access-request'
import type {RuleRequestReviewRequest} from '@/types/auto-assign-rule-request'
import type {RestrictionRuleRequest} from '@/types/restriction-rule-request'
import {formatDateOnly, formatTimestamp} from '@/lib/date-utils'
import {routes} from '@/lib/routes'
import {MutedText} from '@/components/shared/MutedText'
import {SmallText} from '@/components/shared/SmallText'
import {IconText} from '@/components/shared/IconText'
import {LoadingState} from '@/components/shared/LoadingState'
import {Icon} from '@/components/shared/Icon'
import {InlineRow} from '@/components/shared/InlineRow'
import {PageLayout, TabPanelContent} from '@/components/shared/PageLayout'
import type {TabDefinition} from '@/components/shared/PageLayout'
import {TimelineItem} from '@/components/shared/TimelineItem'
import {CONCEPT_ICON} from '@/lib/concepts'

const RULE_PREFIX = 'rule:'
const FG_PREFIX = 'fg:'
const SOD_PREFIX = 'sod:'

export function MyApprovals() {
    const services = useServices()
    const {user} = useAuth()
    const i18n = useI18n()

    const itemStatusBadge = (status: RequestItemStatus) => {
        switch (status) {
            case RequestItemStatus.PENDING_APPROVAL:
                return <Badge variant="outline" className="gap-1"><Clock
                    className="h-3 w-3"/>{i18n.myApprovals.statusPending}</Badge>
            case RequestItemStatus.APPROVED:
                return <Badge variant="secondary" className="gap-1"><CheckCircle
                    className="h-3 w-3"/>{i18n.myApprovals.statusApproved}</Badge>
            case RequestItemStatus.FULFILLED:
                return <Badge variant="success" className="gap-1"><CheckCircle
                    className="h-3 w-3"/>{i18n.myApprovals.statusFulfilled}</Badge>
            case RequestItemStatus.REJECTED:
                return <Badge variant="destructive" className="gap-1"><XCircle
                    className="h-3 w-3"/>{i18n.myApprovals.statusRejected}</Badge>
            case RequestItemStatus.CANCELLED:
                return <Badge variant="secondary" className="gap-1">{i18n.myApprovals.statusCancelled}</Badge>
        }
    }
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
    const [approveDialogItem, setApproveDialogItem] = useState<string | null>(null)
    const [rejectDialogItem, setRejectDialogItem] = useState<string | null>(null)
    const batchApproveDialog = useDialog()
    const batchRejectDialog = useDialog()
    const [comment, setComment] = useState('')

    // All four backend endpoints behind MyApprovals (access requests, auto-assign
    // requests, restriction-rule requests, function-group requests) declare
    // `createdAt` in their @SortableFields set, so using AccessRequestControllerMyApprovalsEndpoint
    // as the type carrier is safe — the emitted sort= is accepted by every backend path.
    const {
        page, pageSize, queryString,
        setPage, setPageSize, pageSizeOptions,
        searchAndFilters: arSearchAndFilters,
        setSearchAndFilters: setArSearchAndFilters,
    } = useServerPagination({
        endpoint: AccessRequestControllerMyApprovalsEndpoint,
        defaultSortBy: AccessRequestControllerMyApprovalsEndpoint.sort.CREATED_AT,
        defaultSortDir: SortDir.DESC,
    })

    const {data, isLoading, error} = useQuery({
        queryKey: ['my-approvals', queryString],
        queryFn: () => services.accessRequests.myApprovals(Object.fromEntries(new URLSearchParams(queryString))),
        enabled: !!user,
    })

    const APPROVALS_KEYS = [['my-approvals'], ['my-approvals-count']]

    const approveMutation = useEntityMutation({
        mutationFn: (itemId: string) =>
            services.accessRequests.approveItem(itemId, {comment: comment || undefined}),
        invalidate: APPROVALS_KEYS,
        successMessage: i18n.myApprovals.itemApproved,
        closeDialog: () => setApproveDialogItem(null),
        onSuccess: () => {
            setComment('')
        },
    })

    const rejectMutation = useEntityMutation({
        mutationFn: (itemId: string) =>
            services.accessRequests.rejectItem(itemId, {comment}),
        invalidate: APPROVALS_KEYS,
        successMessage: i18n.myApprovals.itemRejected,
        closeDialog: () => setRejectDialogItem(null),
        onSuccess: () => {
            setComment('')
        },
    })

    const batchApproveMutation = useEntityMutation({
        mutationFn: (req: BatchApproveRequest) =>
            services.accessRequests.batchApprove(req),
        invalidate: APPROVALS_KEYS,
        successMessage: i18n.myApprovals.batchApproved,
        closeDialog: batchApproveDialog.close,
        onSuccess: () => {
            setSelectedIds(new Set())
            setComment('')
        },
    })

    const batchRejectMutation = useEntityMutation({
        mutationFn: (req: BatchRejectRequest) =>
            services.accessRequests.batchReject(req),
        invalidate: APPROVALS_KEYS,
        successMessage: i18n.myApprovals.batchRejected,
        closeDialog: batchRejectDialog.close,
        onSuccess: () => {
            setSelectedIds(new Set())
            setComment('')
        },
    })

    const pendingItems = data?.content.filter(i => i.status === RequestItemStatus.PENDING_APPROVAL) || []
    const allPendingSelected = pendingItems.length > 0 && pendingItems.every(i => selectedIds.has(i.id))

    const toggleSelect = (id: string) => {
        const next = new Set(selectedIds)
        if (next.has(id)) {
            next.delete(id)
        } else {
            next.add(id)
        }
        setSelectedIds(next)
    }

    const toggleSelectAll = () => {
        if (allPendingSelected) {
            setSelectedIds(new Set())
        } else {
            setSelectedIds(new Set(pendingItems.map(i => i.id)))
        }
    }

    // Rule request approvals
    const {data: ruleData, isLoading: ruleLoading, error: ruleError} = useQuery({
        queryKey: ['rule-request-approvals', page, pageSize],
        queryFn: () => services.autoAssignRules.myApprovals(Object.fromEntries(new URLSearchParams(queryString))),
        enabled: !!user,
    })

    const ruleApproveMutation = useEntityMutation({
        mutationFn: ({requestId, data: reviewData}: {requestId: string, data: RuleRequestReviewRequest}) =>
            services.autoAssignRules.approveRequest(requestId, reviewData),
        invalidate: [['my-approvals'], ['my-approvals-count'], ['rule-request-approvals'], ['rule-request-approvals-count']],
        successMessage: i18n.myApprovals.ruleRequestApproved,
        closeDialog: () => setApproveDialogItem(null),
        onSuccess: () => {
            setComment('')
        },
    })

    const ruleRejectMutation = useEntityMutation({
        mutationFn: ({requestId, data: reviewData}: {requestId: string, data: RuleRequestReviewRequest}) =>
            services.autoAssignRules.rejectRequest(requestId, reviewData),
        invalidate: [['my-approvals'], ['my-approvals-count'], ['rule-request-approvals'], ['rule-request-approvals-count']],
        successMessage: i18n.myApprovals.ruleRequestRejected,
        closeDialog: () => setRejectDialogItem(null),
        onSuccess: () => {
            setComment('')
        },
    })

    // Restriction Rule (SoD) request approvals
    const {data: sodData, isLoading: sodLoading, error: sodError} = useQuery({
        queryKey: ['sod-request-approvals', page, pageSize],
        queryFn: () => services.restrictionRuleRequests.myApprovals(Object.fromEntries(new URLSearchParams(queryString))),
        enabled: !!user,
    })

    const sodApproveMutation = useEntityMutation({
        mutationFn: ({requestId, reviewComment}: {requestId: string, reviewComment?: string}) =>
            services.restrictionRuleRequests.approve(requestId, {comment: reviewComment || undefined}),
        invalidate: [['my-approvals'], ['my-approvals-count'], ['sod-request-approvals'], ['sod-request-approvals-count']],
        successMessage: i18n.restrictionRuleRequests.requestApproved,
        closeDialog: () => setApproveDialogItem(null),
        onSuccess: () => {
            setComment('')
        },
    })

    const sodRejectMutation = useEntityMutation({
        mutationFn: ({requestId, reviewComment}: {requestId: string, reviewComment: string}) =>
            services.restrictionRuleRequests.reject(requestId, {comment: reviewComment}),
        invalidate: [['my-approvals'], ['my-approvals-count'], ['sod-request-approvals'], ['sod-request-approvals-count']],
        successMessage: i18n.restrictionRuleRequests.requestRejected,
        closeDialog: () => setRejectDialogItem(null),
        onSuccess: () => {
            setComment('')
        },
    })

    // FG request approvals
    const {data: fgData, isLoading: fgLoading, error: fgError} = useQuery({
        queryKey: ['fg-request-approvals', page, pageSize],
        queryFn: () => services.functionGroupRequests.myApprovals(Object.fromEntries(new URLSearchParams(queryString))),
        enabled: !!user,
    })

    const fgApproveMutation = useEntityMutation({
        mutationFn: ({requestId, itemId, approveComment}: {requestId: string, itemId: string, approveComment?: string}) =>
            services.functionGroupRequests.approveItem(requestId, itemId, {comment: approveComment || undefined}),
        invalidate: [['my-approvals'], ['my-approvals-count'], ['fg-request-approvals'], ['fg-request-approvals-count']],
        successMessage: i18n.functionGroupRequests.functionGroupRequestApproved,
        closeDialog: () => setApproveDialogItem(null),
        onSuccess: () => {
            setComment('')
        },
    })

    const fgRejectMutation = useEntityMutation({
        mutationFn: ({requestId, itemId, rejectComment}: {requestId: string, itemId: string, rejectComment: string}) =>
            services.functionGroupRequests.rejectItem(requestId, itemId, {comment: rejectComment}),
        invalidate: [['my-approvals'], ['my-approvals-count'], ['fg-request-approvals'], ['fg-request-approvals-count']],
        successMessage: i18n.functionGroupRequests.functionGroupRequestRejected,
        closeDialog: () => setRejectDialogItem(null),
        onSuccess: () => {
            setComment('')
        },
    })

    const navigate = useNavigate()
    const [activeTab, setActiveTab] = useState('access-requests')

    const batchActions = activeTab === 'access-requests' && selectedIds.size > 0 ? (
        <InlineRow gap="sm">
            <MutedText>
                {i18n.myApprovals.selectedItemCount({count: selectedIds.size})}
            </MutedText>
            <Button size="sm" data-testid="btn-batch-approve" onClick={() => {
                setComment('')
                batchApproveDialog.open()
            }}>
                <Icon icon={CheckCircle} size="xs" className="mr-1"/>
                {i18n.myApprovals.batchApprove}
            </Button>
            <Button size="sm" variant="destructive" data-testid="btn-batch-reject" onClick={() => {
                setComment('')
                batchRejectDialog.open()
            }}>
                <Icon icon={XCircle} size="xs" className="mr-1"/>
                {i18n.myApprovals.batchReject}
            </Button>
        </InlineRow>
    ) : undefined

    const tabs: TabDefinition[] = [
        {
            key: MyApprovalsTabId.AccessRequests,
            label: String(i18n.myApprovals.tabAccessRequests),
            count: data && data.totalElements > 0 ? data.totalElements : undefined,
        },
        {
            key: MyApprovalsTabId.RuleRequests,
            label: String(i18n.myApprovals.tabRuleRequests),
            count: ruleData && ruleData.totalElements > 0 ? ruleData.totalElements : undefined,
        },
        {
            key: MyApprovalsTabId.FgRequests,
            label: String(i18n.functionGroupRequests.tabFunctionGroupRequests),
            count: fgData && fgData.totalElements > 0 ? fgData.totalElements : undefined,
        },
        {
            key: MyApprovalsTabId.SodRequests,
            label: String(i18n.restrictionRuleRequests.tabRestrictionRuleRequests),
            count: sodData && sodData.totalElements > 0 ? sodData.totalElements : undefined,
        },
    ]

    const activeTabLabel = tabs.find(t => t.key === activeTab)?.label ?? ''
    const pageTitle = `${String(i18n.myApprovals.title)}: ${activeTabLabel}`
    const tabDescriptions: Record<string, string> = {
        [MyApprovalsTabId.AccessRequests]: String(i18n.myApprovals.accessRequestsDescription),
        [MyApprovalsTabId.RuleRequests]: String(i18n.myApprovals.ruleRequestsDescription),
        [MyApprovalsTabId.FgRequests]: String(i18n.myApprovals.functionGroupRequestsDescription),
        [MyApprovalsTabId.SodRequests]: String(i18n.myApprovals.restrictionRuleRequestsDescription),
    }
    const pageDescription = tabDescriptions[activeTab] ?? String(i18n.myApprovals.itemsPendingApproval)

    return (
        <PageLayout
            data-testid="my-approvals-page"
            variant="tabbed-list"
            icon={CONCEPT_ICON.myApproval}
            title={pageTitle}
            description={pageDescription}
            actions={batchActions}
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={setActiveTab}
        >
            {activeTab === 'access-requests' && (
                <TabPanelContent
                    data-testid="approvals-access-requests-panel"
                    filters={
                        <UnifiedSearchBar
                            contract={AccessRequestControllerMyApprovalsEndpoint}
                            value={arSearchAndFilters}
                            onChange={setArSearchAndFilters}
                            placeholder={String(i18n.myApprovals.searchPlaceholder)}
                            testIdNamespace="my-approvals"
                        />
                    }
                >
            <ErrorBanner error={error} className="mb-4"/>

            {pendingItems.length > 1 && (
                <InlineRow gap="sm" className="my-3">
                    <Checkbox
                        checked={allPendingSelected}
                        onCheckedChange={toggleSelectAll}
                    />
                    <MutedText>{i18n.myApprovals.selectAll}</MutedText>
                </InlineRow>
            )}

            {isLoading && <LoadingState message={String(i18n.common.loading)}/>}

            {data && data.content.length === 0 && (
                <MutedText block centered className="py-8">{i18n.myApprovals.noPendingApprovals}</MutedText>
            )}

            {/* Approval cards */}
            <Stack gap="sm">
                {data?.content.map(item => (
                    <ApprovalCard
                        key={item.id}
                        item={item}
                        selected={selectedIds.has(item.id)}
                        onToggleSelect={() => toggleSelect(item.id)}
                        onApprove={() => {
                            setComment('')
                            setApproveDialogItem(item.id)
                        }}
                        onReject={() => {
                            setComment('')
                            setRejectDialogItem(item.id)
                        }}
                        statusBadge={itemStatusBadge(item.status)}
                        i18n={i18n}
                    />
                ))}
            </Stack>

            {data && data.totalElements > 0 && (
                <Pagination
                    page={page}
                    pageSize={pageSize}
                    totalItems={data.totalElements}
                    totalPages={data.totalPages}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                    pageSizeOptions={pageSizeOptions}
                />
            )}
                </TabPanelContent>
            )}

            {activeTab === 'rule-requests' && (
                <TabPanelContent data-testid="approvals-rule-requests-panel">
                    <ErrorBanner error={ruleError} className="mb-4"/>

                    {ruleLoading && <LoadingState message={String(i18n.common.loading)}/>}

                    {ruleData && ruleData.content.length === 0 && (
                        <MutedText block centered className="py-8">{i18n.myApprovals.noRuleRequestApprovals}</MutedText>
                    )}

                    <Stack gap="sm">
                        {ruleData?.content.map(request => (
                            <Card key={request.id} className="cursor-pointer hover:bg-muted/50" data-testid="rule-approval-card"
                                  onClick={() => navigate(routes.accessGovernance.autoAssignRuleRequests.detail(request.id))}>
                                <CardContent className="pt-4 pb-3">
                                    <InlineRow gap="lg" className="items-start">
                                        <ContentArea>
                                            <InlineRow justify="between">
                                                <InlineRow gap="sm" className="min-w-0">
                                                    <Icon icon={Wand2} muted/>
                                                    <Strong truncate>{request.name}</Strong>
                                                    <Badge variant="outline">{request.requestType}</Badge>
                                                    <Badge variant="outline" className="gap-1">
                                                        <Clock className="h-3 w-3"/>
                                                        {i18n.myApprovals.statusPending}
                                                    </Badge>
                                                </InlineRow>
                                                <InlineRow gap="sm" className="shrink-0" onClick={e => e.stopPropagation()}>
                                                    <Button size="sm" variant="outline" data-testid={`btn-approve-rule-${request.id}`}
                                                            onClick={() => {
                                                                setComment('')
                                                                setApproveDialogItem(`${RULE_PREFIX}${request.id}`)
                                                            }}>
                                                        <Icon icon={CheckCircle} size="xs" className="mr-1"/>
                                                        {i18n.common.approve}
                                                    </Button>
                                                    <Button size="sm" variant="destructive" data-testid={`btn-reject-rule-${request.id}`}
                                                            onClick={() => {
                                                                setComment('')
                                                                setRejectDialogItem(`${RULE_PREFIX}${request.id}`)
                                                            }}>
                                                        <Icon icon={XCircle} size="xs" className="mr-1"/>
                                                        {i18n.common.reject}
                                                    </Button>
                                                </InlineRow>
                                            </InlineRow>
                                            <InlineRow gap="lg" className="mt-1.5">
                                                <MutedText>{request.owners.map(o => o.ownerPartyDisplayName).join(', ')}</MutedText>
                                                <MutedText size="xs">{formatDateOnly(request.createdAt)}</MutedText>
                                            </InlineRow>
                                        </ContentArea>
                                    </InlineRow>
                                </CardContent>
                            </Card>
                        ))}
                    </Stack>

                    {ruleData && ruleData.totalElements > 0 && (
                        <Pagination
                            page={page}
                            pageSize={pageSize}
                            totalItems={ruleData.totalElements}
                            totalPages={ruleData.totalPages}
                            onPageChange={setPage}
                            onPageSizeChange={setPageSize}
                            pageSizeOptions={pageSizeOptions}
                        />
                    )}
                </TabPanelContent>
            )}

            {activeTab === 'fg-requests' && (
                <TabPanelContent data-testid="approvals-fg-requests-panel">
                    <ErrorBanner error={fgError} className="mb-4"/>

                    {fgLoading && <LoadingState message={String(i18n.common.loading)}/>}

                    {fgData && fgData.content.length === 0 && (
                        <MutedText block centered className="py-8">{i18n.functionGroupRequests.noFunctionGroupRequestApprovals}</MutedText>
                    )}

                    <Stack gap="sm">
                        {fgData?.content.map(item => (
                            <Card key={item.id} className="cursor-pointer hover:bg-muted/50" data-testid="fg-approval-card"
                                  onClick={() => navigate(routes.accessGovernance.functionGroupRequests.detail(item.requestId))}>
                                <CardContent className="pt-4 pb-3">
                                    <InlineRow gap="lg" className="items-start">
                                        <ContentArea>
                                            <InlineRow justify="between">
                                                <InlineRow gap="sm" className="min-w-0">
                                                    <Icon icon={Layers} muted/>
                                                    <Strong truncate>{item.functionGroup.displayName}</Strong>
                                                    <Badge variant="outline" className="gap-1">
                                                        <Clock className="h-3 w-3"/>
                                                        {i18n.myApprovals.statusPending}
                                                    </Badge>
                                                </InlineRow>
                                                <InlineRow gap="sm" className="shrink-0" onClick={e => e.stopPropagation()}>
                                                    <Button size="sm" variant="outline" data-testid={`btn-approve-fg-${item.id}`}
                                                            onClick={() => {
                                                                setComment('')
                                                                setApproveDialogItem(`${FG_PREFIX}${item.requestId}:${item.id}`)
                                                            }}>
                                                        <Icon icon={CheckCircle} size="xs" className="mr-1"/>
                                                        {i18n.common.approve}
                                                    </Button>
                                                    <Button size="sm" variant="destructive" data-testid={`btn-reject-fg-${item.id}`}
                                                            onClick={() => {
                                                                setComment('')
                                                                setRejectDialogItem(`${FG_PREFIX}${item.requestId}:${item.id}`)
                                                            }}>
                                                        <Icon icon={XCircle} size="xs" className="mr-1"/>
                                                        {i18n.common.reject}
                                                    </Button>
                                                </InlineRow>
                                            </InlineRow>
                                            <InlineRow gap="lg" className="mt-1.5">
                                                <IconText icon={User} size="sm">
                                                    <MutedText>{i18n.myApprovals.requestedBy} {item.requester.displayName}</MutedText>
                                                </IconText>
                                                {item.requester.displayName !== item.beneficiary.displayName && (
                                                    <MutedText>{i18n.myApprovals.requestedFor} {item.beneficiary.displayName}</MutedText>
                                                )}
                                                <MutedText>
                                                    {item.expiresAt
                                                        ? i18n.accessRequests.untilDate({date: formatDateOnly(item.expiresAt)})
                                                        : i18n.myApprovals.permanent
                                                    }
                                                </MutedText>
                                                <MutedText size="xs">{formatDateOnly(item.createdAt)}</MutedText>
                                            </InlineRow>

                                            {/* Details */}
                                            <Stack gap="sm" topBorder className="mt-2">
                                                {/* Justification */}
                                                <InlineRow gap="sm" className="items-start text-sm">
                                                    <Icon icon={MessageSquare} size="xs" muted className="mt-1"/>
                                                    <div>
                                                        <MutedText>{i18n.accessRequests.justification}:{' '}</MutedText>
                                                        <SmallText className="mt-0.5">{item.justification}</SmallText>
                                                    </div>
                                                </InlineRow>

                                                {/* Approvers — when the viewer is acting as a deputy, this shows the
                                                    original approver(s) so they know whose decision they're standing in for. */}
                                                {item.assignedApprovers.length > 0 && (
                                                    <SmallText>
                                                        <MutedText>{i18n.accessRequests.approvers}</MutedText>
                                                        <InlineRow wrap gap="xs" className="mt-1">
                                                            {item.assignedApprovers.map(a => (
                                                                <Badge key={a.id} variant="outline" className="text-xs">
                                                                    {a.approver.displayName}
                                                                </Badge>
                                                            ))}
                                                        </InlineRow>
                                                    </SmallText>
                                                )}

                                                {/* Prior decisions */}
                                                {item.approvals.length > 0 && (
                                                    <Stack gap="xs">
                                                        {item.approvals.map(a => (
                                                            <TimelineItem
                                                                key={a.id}
                                                                // eslint-disable-next-line i18next/no-literal-string -- Tailwind border-color class
                                                                borderColorClass={ApprovalDecisionBadge.of(a.decision)?.borderClass ?? 'border-muted'}
                                                                className="text-sm"
                                                            >
                                                                <Stack gap="xs">
                                                                    <Strong>{a.approver.displayName}</Strong>
                                                                    <MutedText> {a.decision.toLowerCase()} </MutedText>
                                                                    <MutedText size="xs">{formatTimestamp(a.decidedAt)}</MutedText>
                                                                    {a.comment && (
                                                                        <MutedText className="mt-0.5 block">{a.comment}</MutedText>
                                                                    )}
                                                                </Stack>
                                                            </TimelineItem>
                                                        ))}
                                                    </Stack>
                                                )}
                                            </Stack>
                                        </ContentArea>
                                    </InlineRow>
                                </CardContent>
                            </Card>
                        ))}
                    </Stack>

                    {fgData && fgData.totalElements > 0 && (
                        <Pagination
                            page={page}
                            pageSize={pageSize}
                            totalItems={fgData.totalElements}
                            totalPages={fgData.totalPages}
                            onPageChange={setPage}
                            onPageSizeChange={setPageSize}
                            pageSizeOptions={pageSizeOptions}
                        />
                    )}
                </TabPanelContent>
            )}

            {activeTab === 'sod-requests' && (
                <TabPanelContent data-testid="approvals-sod-requests-panel">
                    <ErrorBanner error={sodError} className="mb-4"/>

                    {sodLoading && <LoadingState message={String(i18n.common.loading)}/>}

                    {sodData && sodData.content.length === 0 && (
                        <MutedText block centered className="py-8" data-testid="sod-requests-empty-state">
                            {i18n.restrictionRuleRequests.noRestrictionRuleRequestApprovals}
                        </MutedText>
                    )}

                    <Stack gap="sm">
                        {sodData?.content.map((request: RestrictionRuleRequest) => (
                            <Card key={request.id} className="cursor-pointer hover:bg-muted/50" data-testid="sod-approval-card"
                                  onClick={() => navigate(routes.accessGovernance.restrictionRuleRequests.detail(request.id))}>
                                <CardContent className="pt-4 pb-3">
                                    <InlineRow gap="lg" className="items-start">
                                        <ContentArea>
                                            <InlineRow justify="between">
                                                <InlineRow gap="sm" className="min-w-0">
                                                    <Icon icon={Shield} muted/>
                                                    <Strong truncate>{request.name}</Strong>
                                                    <Badge variant="outline">{request.requestType}</Badge>
                                                    <Badge variant="outline" className="gap-1">
                                                        <Clock className="h-3 w-3"/>
                                                        {i18n.myApprovals.statusPending}
                                                    </Badge>
                                                </InlineRow>
                                                <InlineRow gap="sm" className="shrink-0" onClick={e => e.stopPropagation()}>
                                                    <Button size="sm" variant="outline" data-testid={`btn-approve-sod-${request.id}`}
                                                            onClick={() => {
                                                                setComment('')
                                                                setApproveDialogItem(`${SOD_PREFIX}${request.id}`)
                                                            }}>
                                                        <Icon icon={CheckCircle} size="xs" className="mr-1"/>
                                                        {i18n.common.approve}
                                                    </Button>
                                                    <Button size="sm" variant="destructive" data-testid={`btn-reject-sod-${request.id}`}
                                                            onClick={() => {
                                                                setComment('')
                                                                setRejectDialogItem(`${SOD_PREFIX}${request.id}`)
                                                            }}>
                                                        <Icon icon={XCircle} size="xs" className="mr-1"/>
                                                        {i18n.common.reject}
                                                    </Button>
                                                </InlineRow>
                                            </InlineRow>
                                            <InlineRow gap="lg" className="mt-1.5">
                                                <MutedText>{request.owners.map(o => o.ownerPartyName).join(', ')}</MutedText>
                                                <MutedText size="xs">{formatDateOnly(request.createdAt)}</MutedText>
                                            </InlineRow>
                                        </ContentArea>
                                    </InlineRow>
                                </CardContent>
                            </Card>
                        ))}
                    </Stack>

                    {sodData && sodData.totalElements > 0 && (
                        <Pagination
                            page={page}
                            pageSize={pageSize}
                            totalItems={sodData.totalElements}
                            totalPages={sodData.totalPages}
                            onPageChange={setPage}
                            onPageSizeChange={setPageSize}
                            pageSizeOptions={pageSizeOptions}
                        />
                    )}
                </TabPanelContent>
            )}

            {/* Single Approve Dialog */}
            <Dialog open={!!approveDialogItem} onOpenChange={() => setApproveDialogItem(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {approveDialogItem?.startsWith(FG_PREFIX)
                                ? i18n.functionGroupRequests.approveFunctionGroupRequest
                                : approveDialogItem?.startsWith(SOD_PREFIX)
                                    ? i18n.restrictionRuleRequests.approveDialogTitle
                                    : approveDialogItem?.startsWith(RULE_PREFIX)
                                        ? i18n.myApprovals.approveRuleRequest
                                        : i18n.myApprovals.approveAccessRequest}
                        </DialogTitle>
                    </DialogHeader>
                    <DialogForm onSubmit={(e) => {
                        e.preventDefault()
                        if (!approveDialogItem) return
                        if (approveDialogItem.startsWith(FG_PREFIX)) {
                            const [requestId, itemId] = approveDialogItem.slice(FG_PREFIX.length).split(':')
                            fgApproveMutation.mutate({requestId, itemId, approveComment: comment || undefined})
                        } else if (approveDialogItem.startsWith(SOD_PREFIX)) {
                            sodApproveMutation.mutate({requestId: approveDialogItem.slice(SOD_PREFIX.length), reviewComment: comment || undefined})
                        } else if (approveDialogItem.startsWith(RULE_PREFIX)) {
                            ruleApproveMutation.mutate({requestId: approveDialogItem.slice(RULE_PREFIX.length), data: {comment: comment || undefined}})
                        } else {
                            approveMutation.mutate(approveDialogItem)
                        }
                    }}>
                        <Stack>
                            <div>
                                <Label htmlFor="approve-comment">{i18n.accessRequests.commentOptional}</Label>
                                <Textarea id="approve-comment" value={comment}
                                          onChange={e => setComment(e.target.value)}
                                          placeholder={i18n.accessRequests.commentPlaceholder}/>
                            </div>
                        </Stack>
                        <SaveCancelFooter
                            testIdPrefix="approve"
                            onCancel={() => setApproveDialogItem(null)}
                            saving={approveMutation.isPending || ruleApproveMutation.isPending || fgApproveMutation.isPending || sodApproveMutation.isPending}
                            saveLabel={i18n.common.approve}
                            savingLabel={i18n.accessRequests.approving}
                        />
                    </DialogForm>
                </DialogContent>
            </Dialog>

            {/* Single Reject Dialog */}
            <Dialog open={!!rejectDialogItem} onOpenChange={() => setRejectDialogItem(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {rejectDialogItem?.startsWith(FG_PREFIX)
                                ? i18n.functionGroupRequests.rejectFunctionGroupRequest
                                : rejectDialogItem?.startsWith(SOD_PREFIX)
                                    ? i18n.restrictionRuleRequests.rejectDialogTitle
                                    : rejectDialogItem?.startsWith(RULE_PREFIX)
                                        ? i18n.myApprovals.rejectRuleRequest
                                        : i18n.myApprovals.rejectAccessRequest}
                        </DialogTitle>
                    </DialogHeader>
                    <DialogForm onSubmit={(e) => {
                        e.preventDefault()
                        if (!rejectDialogItem) return
                        if (rejectDialogItem.startsWith(FG_PREFIX)) {
                            const [requestId, itemId] = rejectDialogItem.slice(FG_PREFIX.length).split(':')
                            fgRejectMutation.mutate({requestId, itemId, rejectComment: comment})
                        } else if (rejectDialogItem.startsWith(SOD_PREFIX)) {
                            sodRejectMutation.mutate({requestId: rejectDialogItem.slice(SOD_PREFIX.length), reviewComment: comment})
                        } else if (rejectDialogItem.startsWith(RULE_PREFIX)) {
                            ruleRejectMutation.mutate({requestId: rejectDialogItem.slice(RULE_PREFIX.length), data: {comment}})
                        } else {
                            rejectMutation.mutate(rejectDialogItem)
                        }
                    }}>
                        <Stack>
                            <div>
                                <Label htmlFor="reject-comment">{i18n.accessRequests.reasonRequired}</Label>
                                <Textarea id="reject-comment" value={comment}
                                          onChange={e => setComment(e.target.value)}
                                          placeholder={i18n.accessRequests.rejectReasonPlaceholder}/>
                            </div>
                        </Stack>
                        <SaveCancelFooter
                            testIdPrefix="reject"
                            onCancel={() => setRejectDialogItem(null)}
                            saving={rejectMutation.isPending || ruleRejectMutation.isPending || fgRejectMutation.isPending || sodRejectMutation.isPending}
                            disabled={!comment.trim()}
                            saveLabel={i18n.common.reject}
                            savingLabel={i18n.accessRequests.rejecting}
                            destructive
                        />
                    </DialogForm>
                </DialogContent>
            </Dialog>

            {/* Batch Approve Dialog */}
            <Dialog open={batchApproveDialog.isOpen} onOpenChange={() => batchApproveDialog.close()}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.myApprovals.batchApproveTitle({count: selectedIds.size})}</DialogTitle>
                    </DialogHeader>
                    <DialogForm onSubmit={(e) => { e.preventDefault(); batchApproveMutation.mutate({ itemIds: Array.from(selectedIds), comment: comment || undefined }) }}>
                        <Stack>
                            <p><MutedText>{i18n.myApprovals.batchApproveConfirm({count: selectedIds.size})}</MutedText></p>
                            <div>
                                <Label htmlFor="batch-approve-comment">{i18n.accessRequests.commentOptional}</Label>
                                <Textarea id="batch-approve-comment" value={comment}
                                          onChange={e => setComment(e.target.value)}
                                          placeholder={i18n.accessRequests.commentPlaceholder}/>
                            </div>
                        </Stack>
                        <SaveCancelFooter
                            testIdPrefix="batch-approve"
                            onCancel={batchApproveDialog.close}
                            saving={batchApproveMutation.isPending}
                            saveLabel={i18n.myApprovals.batchApprove}
                            savingLabel={i18n.accessRequests.approving}
                        />
                    </DialogForm>
                </DialogContent>
            </Dialog>

            {/* Batch Reject Dialog */}
            <Dialog open={batchRejectDialog.isOpen} onOpenChange={() => batchRejectDialog.close()}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.myApprovals.batchRejectTitle({count: selectedIds.size})}</DialogTitle>
                    </DialogHeader>
                    <DialogForm onSubmit={(e) => { e.preventDefault(); batchRejectMutation.mutate({ itemIds: Array.from(selectedIds), comment }) }}>
                        <Stack>
                            <p><MutedText>{i18n.myApprovals.batchRejectConfirm({count: selectedIds.size})}</MutedText></p>
                            <div>
                                <Label htmlFor="batch-reject-comment">{i18n.accessRequests.reasonRequired}</Label>
                                <Textarea id="batch-reject-comment" value={comment}
                                          onChange={e => setComment(e.target.value)}
                                          placeholder={i18n.accessRequests.rejectReasonPlaceholder}/>
                            </div>
                        </Stack>
                        <SaveCancelFooter
                            testIdPrefix="batch-reject"
                            onCancel={batchRejectDialog.close}
                            saving={batchRejectMutation.isPending}
                            disabled={!comment.trim()}
                            saveLabel={i18n.myApprovals.batchReject}
                            savingLabel={i18n.accessRequests.rejecting}
                            destructive
                        />
                    </DialogForm>
                </DialogContent>
            </Dialog>
        </PageLayout>
    )
}

interface ApprovalCardProps {
    item: AccessRequestItem
    selected: boolean
    onToggleSelect: () => void
    onApprove: () => void
    onReject: () => void
    statusBadge: React.ReactNode
    // eslint-disable-next-line i18next/no-literal-string
    i18n: ReturnType<typeof import('@/i18n').useI18n>
}

function ApprovalCard({item, selected, onToggleSelect, onApprove, onReject, statusBadge, i18n}: ApprovalCardProps) {
    const isPending = item.status === RequestItemStatus.PENDING_APPROVAL

    return (
        <Card className={selected ? 'ring-2 ring-primary' : ''} data-testid="approval-card">
            <CardContent className="pt-4 pb-3">
                {/* Main row */}
                <InlineRow gap="lg" className="items-start">
                    {isPending && (
                        <Checkbox
                            checked={selected}
                            onCheckedChange={onToggleSelect}
                            className="mt-1"
                        />
                    )}
                    <ContentArea>
                        {/* Top line: profile name + status + actions */}
                        <InlineRow justify="between">
                            <InlineRow gap="sm" className="min-w-0">
                                <Icon icon={KeyRound} muted/>
                                <Strong truncate>{item.accessProfile.displayName}</Strong>
                                <MutedText className="shrink-0">{item.resource.displayName}</MutedText>
                                {statusBadge}
                            </InlineRow>
                            {isPending && (
                                <InlineRow gap="sm" className="shrink-0">
                                    <Button size="sm" variant="outline" onClick={onApprove} data-testid={`btn-approve-${item.id}`}>
                                        <Icon icon={CheckCircle} size="xs" className="mr-1"/>
                                        {i18n.common.approve}
                                    </Button>
                                    <Button size="sm" variant="destructive" onClick={onReject} data-testid={`btn-reject-${item.id}`}>
                                        <Icon icon={XCircle} size="xs" className="mr-1"/>
                                        {i18n.common.reject}
                                    </Button>
                                </InlineRow>
                            )}
                        </InlineRow>

                        {/* Second line: requester, beneficiary, expiry */}
                        <InlineRow gap="lg" className="mt-1.5">
                            <IconText icon={User} size="sm">
                                <MutedText>{i18n.myApprovals.requestedBy} {item.requester.displayName}</MutedText>
                            </IconText>
                            {item.requester.displayName !== item.beneficiary.displayName && (
                                <MutedText>{i18n.myApprovals.requestedFor} {item.beneficiary.displayName}</MutedText>
                            )}
                            <MutedText>
                                {item.expiresAt
                                    ? i18n.accessRequests.untilDate({date: formatDateOnly(item.expiresAt)})
                                    : i18n.myApprovals.permanent
                                }
                            </MutedText>
                            <MutedText size="xs">
                                {formatDateOnly(item.createdAt)}
                            </MutedText>
                        </InlineRow>

                        {/* Details */}
                        <Stack gap="sm" topBorder className="mt-2">
                            {/* Justification */}
                            <InlineRow gap="sm" className="items-start text-sm">
                                <Icon icon={MessageSquare} size="xs" muted className="mt-1"/>
                                <div>
                                    <MutedText>{i18n.accessRequests.justification}:{' '}</MutedText>
                                    <SmallText className="mt-0.5">{item.justification}</SmallText>
                                </div>
                            </InlineRow>

                            {/* Risk level */}
                            {item.riskLevel && (
                                <SmallText>
                                    <MutedText>{i18n.myApprovals.risk}</MutedText>{' '}
                                    <RiskLevelBadge riskLevel={item.riskLevel} className="text-xs"/>
                                </SmallText>
                            )}

                            {/* Approvers */}
                            {item.assignedApprovers.length > 0 && (
                                <SmallText>
                                    <MutedText>{i18n.accessRequests.approvers}</MutedText>
                                    <InlineRow wrap gap="xs" className="mt-1">
                                        {item.assignedApprovers.map(a => (
                                            <Badge key={a.id} variant="outline" className="text-xs">
                                                {a.approver.displayName}
                                            </Badge>
                                        ))}
                                    </InlineRow>
                                </SmallText>
                            )}

                            {/* Prior decisions */}
                            {item.approvals.length > 0 && (
                                <Stack gap="xs">
                                    {item.approvals.map(a => (
                                        <TimelineItem
                                            key={a.id}
                                            // eslint-disable-next-line i18next/no-literal-string -- Tailwind border-color class
                                            borderColorClass={ApprovalDecisionBadge.of(a.decision)?.borderClass ?? 'border-muted'}
                                            className="text-sm"
                                        >
                                            <Stack gap="xs">
                                                <Strong>{a.approver.displayName}</Strong>
                                                <MutedText> {a.decision.toLowerCase()} </MutedText>
                                                <MutedText size="xs">{formatTimestamp(a.decidedAt)}</MutedText>
                                                {a.comment && (
                                                    <MutedText className="mt-0.5 block">{a.comment}</MutedText>
                                                )}
                                            </Stack>
                                        </TimelineItem>
                                    ))}
                                </Stack>
                            )}
                        </Stack>
                    </ContentArea>
                </InlineRow>
            </CardContent>
        </Card>
    )
}
